import { useCallback, useEffect, useState, type ReactNode } from "react";

import { routeLabel } from "../booking.ts";
import { quoteMoney } from "../money.ts";
import { priceHeldLabel } from "../ride-journey.ts";
import type { QuoteValidation, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { MapHero } from "./Hero.tsx";
import { Note, PhoneShell, Rows } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `3 Quote` and `3b Quote expired` — one read, two screens, and MOB-02 decides which.
 *
 * The design draws them as two states of one moment, and the platform publishes exactly that:
 * `POST /v1/ride-quotes/{id}/validate` answers the quote **and** a `reason` of `valid | expired`.
 * The measurement names that `reason` field as the source of _"That price has expired"_ — the expiry
 * verdict itself rather than a comparison a phone makes against its own clock.
 *
 * **So the screen follows the verdict, not the address.** A rider who opens `?page=quote` on a price
 * that has lapsed is shown `3b`, and the address is corrected to match. The reverse matters more:
 * a phone whose clock is four minutes fast must never send a rider to `3b` over a price that is
 * still held, and a phone four minutes slow must never let them press `Confirm ride` on one that is
 * not. The countdown on the chip runs on this phone's clock and is cosmetic; the verdict is the
 * service's and is not.
 *
 * ## What `3 Quote` draws, and what it cannot
 *
 * | Drawn                                   | From                                              |
 * | --------------------------------------- | ------------------------------------------------- |
 * | `KES 2,100 · XL`                        | MOB-02's fare, MOB-01's `displayName` for the class |
 * | `Price held 4:12`                       | MOB-02's `quote.expiresAt`                        |
 * | `Westlands → JKIA Terminal 1A`          | GEO-02, reverse-geocoding the quote's two points  |
 * | `Est. KES 2,100` / `Final At drop-off`  | MOB-02's fare; the right cell is designed copy    |
 * | `Pay with · M-Pesa ··· 0421`            | **nothing** — no masked instrument is published   |
 * | `Cancel free · Within 2 min`            | **nothing** — no free-cancellation window         |
 * | `Surge · None after booking`            | **nothing** — PRC-03 needs a `policyId` nobody has |
 * | `Confirm ride`                          | MOB-03 `POST /v1/ride-requests`                   |
 *
 * Three of the eight rows are the measurement's three for this screen, and all three are in the one
 * `rowsBox` — so the designed card is drawn with its three rows in their designed order, each
 * carrying the dashed mark instead of a sample value.
 */

export interface QuoteProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly quoteId: string | undefined;
  /** `quote` or `quote-expired`, which is what the address asked for. */
  readonly asked: "quote" | "quote-expired";
  /** Corrects the address when MOB-02's verdict disagrees with it. */
  readonly onScreenChange: (pageId: string) => void;
  /** Booked, which moves to `4 Confirming` carrying the ride request. */
  readonly onBooked: (rideRequestId: string) => void;
  readonly onBack: () => void;
  /** Milliseconds now, injected so the countdown can be put into a known state. */
  readonly now: () => number;
}

export function Quote(props: QuoteProps): ReactNode {
  const { api, quoteId, now } = props;

  const [validation, setValidation] = useState<QuoteValidation | undefined>(undefined);
  const [className, setClassName] = useState<string | null>(null);
  const [places, setPlaces] = useState<{ origin: string | null; destination: string | null }>({
    origin: null,
    destination: null,
  });
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  const [booking, setBooking] = useState(false);
  const [tick, setTick] = useState(() => now());

  const load = useCallback(() => {
    if (quoteId === undefined) return;
    setState({ kind: "loading" });
    api
      .validateQuote(quoteId)
      .then((answer) => {
        setValidation(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.validateQuote])));
  }, [api, quoteId]);

  useEffect(load, [load]);

  /*
   * The class's own name, from the one listing that publishes it.
   *
   * MOB-02's quote carries `product.id` and `product.mode` and no display name, so `· XL` in the
   * title is a join through MOB-01's listing — the same listing `2 Choose` reads. A quote whose
   * product is no longer in the catalogue gets no name rather than its identifier, for the reason
   * EXP-002 gives about `displayName`: printing an identifier where a name belongs tells a reader
   * that the service published a name it did not.
   */
  useEffect(() => {
    const quote = validation?.quote;
    if (quote === undefined) return;
    let current = true;
    api
      .rideProducts()
      .then((products) => {
        if (!current) return;
        setClassName(
          products.find((product) => product.productId === quote.product.id)?.displayName ?? null,
        );
      })
      .catch(() => {
        if (current) setClassName(null);
      });
    return (): void => {
      current = false;
    };
  }, [api, validation]);

  /*
   * The two ends of the route, as place names.
   *
   * MOB-02's quote publishes both coordinates, and GEO-02 is the only operation that turns one into
   * a name. Two requests rather than one, because reverse geocoding is per point — and either may
   * fail independently, which `routeLabel` draws as the half it has.
   */
  useEffect(() => {
    const quote = validation?.quote;
    if (quote === undefined) return;
    let current = true;
    const name = (point: { latitude: number; longitude: number }): Promise<string | null> =>
      api
        // Zero rather than a guess: this is a coordinate the pricing service published for a point a
        // rider chose, not a satellite fix, so there is no accuracy radius to report and claiming one
        // would be inventing a number for a required field.
        .reverseGeocode({ latitude: point.latitude, longitude: point.longitude, accuracyMeters: 0 })
        .then((answer) => answer.address.formatted)
        .catch(() => null);
    void Promise.all([name(quote.origin), name(quote.destination)]).then(
      ([origin, destination]) => {
        if (current) setPlaces({ origin, destination });
      },
    );
    return (): void => {
      current = false;
    };
  }, [api, validation]);

  /*
   * The countdown, once a second, and only while the price is held.
   *
   * It stops at the verdict rather than at zero: MOB-02 decides whether a price is still good, and a
   * timer that ran a chip to `0:00` and left the Confirm button live would be this phone's clock
   * overriding the service's.
   */
  useEffect(() => {
    if (validation?.valid !== true) return;
    const timer = setInterval(() => setTick(now()), 1000);
    return (): void => clearInterval(timer);
  }, [validation, now]);

  /*
   * The verdict decides the screen, and the address is corrected to agree with it.
   *
   * In an effect rather than during render: correcting the address is a parent state change, and a
   * child that performs one while rendering is the React fault that shows up as a warning in
   * development and as a dropped update in production. `replaceState` rather than a push, because
   * this is a correction of where the rider already is rather than a step they took — pushing it
   * would put the screen they could not use into their back button.
   */
  const verdict =
    validation === undefined ? undefined : !validation.valid || validation.reason === "expired";
  useEffect(() => {
    if (verdict === undefined) return;
    const shouldBe = verdict ? "quote-expired" : "quote";
    if (shouldBe !== props.asked) props.onScreenChange(shouldBe);
  }, [verdict, props]);

  const book = useCallback(() => {
    const quote = validation?.quote;
    if (quote === undefined) return;
    setBooking(true);
    api
      /*
       * The key, derived from the quote rather than minted fresh.
       *
       * `4 Confirming`'s own designed note is the requirement: _"If the connection drops we'll check
       * what happened instead of booking twice."_ A random key would make a retry a second booking;
       * keyed on the quote, a retry is the same booking, because booking one immutable priced quote
       * twice is one ride. Document 06 refuses a key reused with materially different terms, which
       * is the guard against this being clever rather than correct.
       */
      .bookRide(quote.id, `ride-request:${quote.id}`)
      .then((answer) => props.onBooked(answer.request.id))
      .catch((error: unknown) => {
        setBooking(false);
        setState(stateForError(error, [SCOPES.book]));
      });
  }, [api, validation, props]);

  if (quoteId === undefined) {
    return (
      <StateScreen
        screen="journey"
        chip={{ class: "unknown", label: "no price" }}
        title="This link names no price"
        sub="A price is a resource with its own identifier, and this address carries none. Choose a ride again to get a fresh one."
      />
    );
  }

  if (state.kind !== "ready" || validation === undefined) {
    return (
      <StateScreen
        screen="journey journey--fare"
        state={state}
        onRetry={load}
        purpose="Checking your price is still held."
      />
    );
  }

  const expired = verdict === true;
  const quote = validation.quote;

  if (expired) {
    return (
      <PhoneShell
        screen="journey"
        hero={<MapHero kind="route" />}
        back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
        safety={{ armed: false }}
        chip={{ class: "closed", label: "expired" }}
        title="That price has expired"
        sub="Prices are held for 5 minutes. Nothing was booked."
        actions={[
          {
            label: "Get a new price",
            glyph: "refresh",
            primary: true,
            onPress: props.onBack,
          },
        ]}
      >
        <Note
          kind="info"
          text={
            className === null
              ? "Your route is kept. One tap for a fresh price."
              : `Your route and ${className} are kept. One tap for a fresh price.`
          }
        />
      </PhoneShell>
    );
  }

  const held = priceHeldLabel(quote.expiresAt, tick);
  const fare = quoteMoney(quote.fare);
  const route = routeLabel(places.origin, places.destination);

  return (
    <PhoneShell
      screen="journey journey--fare"
      hero={<MapHero kind="route" />}
      back={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      safety={{ armed: false }}
      /*
       * `Price held 4:12`.
       *
       * The caution pill is the design's, and it is the right class: a price that is running out is
       * not a failure and is not a settled state. When the countdown has run out on this phone's
       * clock but MOB-02 still says the price is held, the chip says so in words rather than showing
       * `0:00` — the service's verdict is what decides, and a chip counting into negatives would be
       * the phone arguing with it.
       */
      chip={{
        class: "caution",
        label: held === null ? "Price held · checking" : `Price held ${held}`,
      }}
      title={className === null ? fare : `${fare} · ${className}`}
      sub={route ?? "We couldn't name either end of this route"}
      actions={[
        {
          label: booking ? "Booking…" : "Confirm ride",
          glyph: "check",
          primary: true,
          ...(booking || !props.scopes.has(SCOPES.book)
            ? {
                unavailable: booking
                  ? "Your booking is on its way."
                  : "Your sign-in doesn't allow booking a ride. It needs ride-request:create.",
              }
            : { onPress: book }),
        },
      ]}
    >
      <div className="fare">
        <div className="fare__cell">
          <span className="fare__label">Est.</span>
          <span className="fare__value">{fare}</span>
        </div>
        <div className="fare__cell fare__cell--final">
          <span className="fare__label">Final</span>
          {/*
           * `At drop-off` is the design's own words rather than a figure, and is kept verbatim.
           *
           * It is the one cell of the three fare boxes in this file that is copy rather than data,
           * which is why it is not an absence: the design is not promising a number here, it is
           * telling the rider when the number arrives.
           */}
          <span className="fare__value">At drop-off</span>
        </div>
      </div>

      <Rows
        rows={[
          {
            key: "Pay with",
            value: null,
            because: "no stored instrument published",
          },
          {
            key: "Cancel free",
            value: null,
            because: "no free-cancellation window published",
          },
          {
            key: "Surge",
            value: null,
            because: "the pricing policy needs a policyId nobody holds",
          },
        ]}
      />
    </PhoneShell>
  );
}
